/**
 * Simplified Admin Settings Manager
 * 
 * Provides focused admin settings management with consistent patterns
 * Eliminates complex conditional logic and improves maintainability
 */

import type { AdminSettingsData } from '@/lib/types';
import { sampleAdminSettings as initialSampleAdminSettings } from '@/lib/sample-data';
import { isSampleDataMode } from '@/lib/config/env';
import { deepClone, generateCacheKey } from '@/lib/utils/cloning';

export interface AdminSettingsFilters {
  organizationId?: string;
  tenantId?: string;
}

export interface AdminSettingsUpdateData extends Partial<AdminSettingsData> {
  // Additional update-specific fields can be added here
  updateReason?: string;
}

const CONFIG = {
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
  SAMPLE_DATA_DELAY: 100, // Simulate API delay
} as const;

interface Cache {
  data: AdminSettingsData;
  timestamp: number;
  key: string;
}

/**
 * Admin settings manager with focused responsibility
 * Eliminates complex conditional logic and reduces cyclomatic complexity
 */
class AdminSettingsManager {
  private cache: Cache | null = null;
  private currentSettings: AdminSettingsData = deepClone(initialSampleAdminSettings);

  /**
   * Check if cache is valid for given filters
   */
  private isCacheValid(cacheKey: string): boolean {
    if (!this.cache) return false;
    
    const isExpired = (Date.now() - this.cache.timestamp) > CONFIG.CACHE_DURATION;
    const keyChanged = this.cache.key !== cacheKey;
    
    return !isExpired && !keyChanged;
  }

  /**
   * Get admin settings with caching
   */
  async getAdminSettings(filters: AdminSettingsFilters = {}): Promise<AdminSettingsData> {
    const cacheKey = generateCacheKey(filters);
    
    if (this.isCacheValid(cacheKey)) {
      return deepClone(this.cache!.data);
    }

    return this.withDelay(async () => {
      try {
        // Apply any organization/tenant filtering if needed
        let settings = deepClone(this.currentSettings);
        
        // In a real implementation, you might filter based on organization/tenant
        if (filters.organizationId || filters.tenantId) {
          // Apply specific organization/tenant settings override
          settings = this.applyOrganizationSettings(settings, filters);
        }

        // Update cache
        this.cache = {
          data: deepClone(settings),
          timestamp: Date.now(),
          key: cacheKey
        };

        return settings;
      } catch (error) {
        console.error('Error loading admin settings:', error);
        return this.getFallbackSettings();
      }
    });
  }

  /**
   * Update admin settings
   */
  async updateAdminSettings(updates: Partial<AdminSettingsData>): Promise<AdminSettingsData> {
    return this.withDelay(async () => {
      try {
        if (isSampleDataMode()) {
          // Update cached settings
          this.currentSettings = {
            ...this.currentSettings,
            ...updates
          };
          
          console.log('AdminSettingsManager: Sample data mode - settings updated in memory');
          return deepClone(this.currentSettings);
        }

        // In production, would update settings via Firebase or other persistent storage
        console.log('AdminSettingsManager: Production mode - would update in Firebase');
        
        // Update cached settings for immediate response
        this.currentSettings = {
          ...this.currentSettings,
          ...updates
        };
        
        // Clear cache to force refresh
        this.cache = null;
        
        return deepClone(this.currentSettings);
      } catch (error) {
        console.error('Error updating admin settings:', error);
        throw new Error('Failed to update admin settings');
      }
    });
  }

  /**
   * Update currency settings specifically
   */
  async updateCurrencySettings(currencyUpdate: Partial<AdminSettingsData['currency']>): Promise<AdminSettingsData> {
    const fullCurrencyUpdate = {
      ...this.currentSettings.currency,
      ...currencyUpdate
    };
    return this.updateAdminSettings({ currency: fullCurrencyUpdate });
  }

  /**
   * Reset settings to defaults
   */
  async resetSettings(): Promise<AdminSettingsData> {
    return this.withDelay(async () => {
      this.currentSettings = deepClone(initialSampleAdminSettings);
      this.cache = null;
      return deepClone(this.currentSettings);
    });
  }

  /**
   * Get specific setting value
   */
  async getSetting<K extends keyof AdminSettingsData>(
    key: K,
    filters: AdminSettingsFilters = {}
  ): Promise<AdminSettingsData[K]> {
    const settings = await this.getAdminSettings(filters);
    return settings[key];
  }

  /**
   * Update specific setting value
   */
  async updateSetting<K extends keyof AdminSettingsData>(
    key: K,
    value: AdminSettingsData[K],
    _filters: AdminSettingsFilters = {}
  ): Promise<AdminSettingsData> {
    const update = { [key]: value } as AdminSettingsUpdateData;
    return this.updateAdminSettings(update);
  }

  /**
   * Get currency settings
   */
  async getCurrencySettings(filters: AdminSettingsFilters = {}): Promise<AdminSettingsData['currency']> {
    return this.getSetting('currency', filters);
  }

  /**
   * Check if a feature is enabled
   */
  async isFeatureEnabled(_featureKey: string, _filters: AdminSettingsFilters = {}): Promise<boolean> {
    // Implement feature flag logic here
    // For now, return true for all features as a fallback
    return true;
  }

  /**
   * Apply organization-specific settings overrides
   */
  private applyOrganizationSettings(
    settings: AdminSettingsData,
    filters: AdminSettingsFilters
  ): AdminSettingsData {
    // In a real implementation, you would fetch organization-specific settings
    // and override the base settings. For now, return the base settings.
    
    // Example: Organization-specific currency overrides
    if (filters.organizationId) {
      // Could override currency based on organization's locale
      // settings.currency = getOrganizationCurrency(filters.organizationId);
    }

    return settings;
  }

  /**
   * Validate settings updates
   */
  private validateSettingsUpdates(updates: AdminSettingsUpdateData): void {
    // Validate currency updates
    if (updates.currency) {
      if (updates.currency.code && !/^[A-Z]{3}$/.test(updates.currency.code)) {
        throw new Error('Currency code must be a 3-letter uppercase string');
      }
    }

    // Add more validation rules as needed
  }

  /**
   * Get fallback settings when errors occur
   */
  private getFallbackSettings(): AdminSettingsData {
    return deepClone(initialSampleAdminSettings);
  }

  /**
   * Add simulated delay for realistic behavior
   */
  private async withDelay<T>(operation: () => Promise<T>): Promise<T> {
    const result = await operation();
    await new Promise(resolve => setTimeout(resolve, CONFIG.SAMPLE_DATA_DELAY));
    return result;
  }
}

export const adminSettingsManager = new AdminSettingsManager(); 
